import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';
import { Divider, Stack, Typography } from '@mui/material';

import { getAttribute, MONEY_CONTAINER_TYPE, withdrawToBank } from 'redux/slices/wallet/moneyContainerSlice';
import { AppDispatch, RootState } from 'redux/store';
import { formatAmount } from 'utils/helpers';
import CustomButton from 'components/Common/Button';
import CardWrapper from 'components/Common/CardWrapper';
import InfoModal from 'components/Common/InfoModal';
import TextInput from 'components/Common/TextInput';
import ErrorMessage from 'components/Common/ErrorMessage';
import { TransactionTypes } from 'types/transaction';
import { useNavigate } from 'react-router-dom';
import Routes from 'constants/routes';

const validationSchema = (balance: number) =>
    Yup.object({
        amount: Yup.string()
            .required('Amount is required')
            .test('min-amount', 'Amount must be greater than zero', (value) => parseFloat(value.replace('$', '')) >= 0.01)
            .test('max-amount', `Amount must be less than ${balance}`, (value) => {
                const _value = parseFloat(value.replace('$', '')) <= balance * 100;
                return _value;
            })
            .label('Amount must be greater than zero and less than balance'),
        to: Yup.number().required(),
    });

function WithdrawToBank() {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const [modalOpen, setModalOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const { balance, bankAccounts, sva } = useSelector((state: RootState) => ({
        balance: state.wallet?.merchantBalance?.balance,
        bankAccounts: state.wallet.moneyContainer.records.filter((mc) => mc.type === MONEY_CONTAINER_TYPE.BANK_ACCOUNT),
        sva: state.wallet.moneyContainer.defaultSVA,
    }));

    const {
        getValues,
        control,
        handleSubmit,
        setValue,
        formState: { errors },
        trigger,
        watch,
    } = useForm({
        resolver: yupResolver(validationSchema(balance)),
        defaultValues: {
            amount: '',
            to: bankAccounts?.[0]?.containerId || 0,
        },
    });

    const bankAccount = bankAccounts[0];
    let bankName = '';
    if (bankAccount) {
        const _name = getAttribute(bankAccount, 'attributes', 'ACCOUNT_NAME');
        bankName = _name + ' **' + bankAccount?.last4AccountDigits;
    }

    const onSubmit = () => {
        setModalOpen(true);
    };

    const handleConfirm = async () => {
        const { amount, to } = getValues();
        const data = {
            amount,
            to: String(to),
            from: sva ? String(sva) : '',
            transactionType: TransactionTypes.CASHOUT_SVA_TO_BANK,
        };
        setLoading(true);
        const { payload } = await dispatch(withdrawToBank(data));
        setLoading(false);
        if (payload?.code === 200 || payload?.contextResponse?.statusCode === 'SUCCESS') {
            navigate(Routes.wallet.parishWallet, { replace: true });
        }
    };

    return (
        <CardWrapper>
            <Typography sx={{ fontSize: 16, mb: 2, fontWeight: 400 }} variant="h1">
                Withdraw to Bank
            </Typography>
            <Divider />
            <Typography sx={{ mt: 2, mb: 4 }} variant="body1">
                Please enter the amount you wish to deposit into your selected account. Once you click “Withdraw” you will not be able to
                undo this transaction. It may take 1-2 business days to see the deposit in your account.
            </Typography>
            <form onSubmit={handleSubmit(onSubmit)}>
                <Stack alignItems="center" justifyContent="center" gap={3}>
                    <TextInput
                        icon={null}
                        label="Withdraw From"
                        value={`Available Balance ${formatAmount(balance || 0)}`}
                        inputProps={{ readOnly: true }}
                    />
                    <TextInput icon={null} label="To" placeholder="" value={bankName} inputProps={{ readOnly: true }} />
                    <Controller
                        name="amount"
                        control={control}
                        render={({ field }) => (
                            <TextInput
                                icon={null}
                                {...field}
                                placeholder="$0.00 "
                                label="Amount"
                                type="currency"
                                onChange={(event) => {
                                    setValue('amount', event.target.value);
                                    trigger('amount');
                                }}
                            />
                        )}
                    />
                    {errors.amount?.message && <ErrorMessage message={errors.amount?.message} sx={{ maxWidth: '443px' }} />}
                    <CustomButton type="submit" disabled={!bankAccount} sx={{ width: { xs: '100%', sm: '50%' } }}>
                        Withdraw
                    </CustomButton>
                </Stack>
            </form>

            {/* Confirm Modal */}
            <InfoModal
                loading={loading}
                onConfirm={handleConfirm}
                onClose={() => setModalOpen(false)}
                isOpen={modalOpen}
                title="Confirm Withdrawal"
            >
                <>
                    <Typography sx={{ fontSize: 18 }}>
                        Are you sure you want to withdraw
                        <br />
                        <strong style={{ marginBottom: 2, fontSize: 20 }}>{formatAmount(watch('amount'), 'cents')}</strong>
                    </Typography>
                    <Typography style={{ whiteSpace: 'nowrap' }}>
                        <p style={{ color: '#1F2937', marginTop: 3, marginBottom: 3 }}>To</p>
                        <strong style={{ fontSize: 20 }}>
                            {(bankAccount?.description + ' **' + bankAccount?.last4AccountDigits).trim()}
                        </strong>
                    </Typography>
                </>
            </InfoModal>
        </CardWrapper>
    );
}

export default WithdrawToBank;
